/**
 * PG-X03 锁屏（PRD 5.17.4"30 分钟无操作锁屏，解锁需密码 + TOTP"、§6.19；spec-identity AA-03）。
 *
 * 外壳（components/layout/AdminLayout）在 ADMIN_SESSION_LOCKED 或会话 locked 时把本组件盖在页面上方：
 * 页面不卸载、已填数据保留（外壳给页面加 inert）；遮罩不透明，只显示员工姓名、密码 + 动态码、「解锁」、「退出登录」。
 * 页面上已打开的对话框在 body 下，不受 inert 影响：锁屏期间在捕获阶段接管 Tab 与 Esc，焦点只在锁屏内移动。
 * 会话已过期（ADMIN_AUTH_REQUIRED）时按退出登录处理。
 */
import { useI18n } from '@nexora/i18n/react';
import { Button, CodeInput, Field, TextInput, isApiError } from '@nexora/ui';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { useAdminErrorText } from '../layout/api/useAdminErrorText';
import type { LockScreenProps } from '../layout/slots';
import { staffApi } from '../staff/api';

const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default function LockScreen({ staffName, onUnlocked, onSignedOut }: LockScreenProps) {
  const { t } = useI18n();
  const errorText = useAdminErrorText();
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{ password?: string; code?: string }>({});
  const [formError, setFormError] = useState<string | null>(null);

  // 焦点移入锁屏；解锁后回到原来的位置
  useEffect(() => {
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    passwordRef.current?.focus();
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (event: KeyboardEvent) => {
      const panel = panelRef.current;
      if (!panel) return;
      if (event.key === 'Escape') {
        // 不让页面上的对话框在锁屏期间响应 Esc
        event.stopPropagation();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (items.length === 0) return;
      event.preventDefault();
      event.stopPropagation();
      const index = items.indexOf(document.activeElement as HTMLElement);
      const next = event.shiftKey ? (index <= 0 ? items.length - 1 : index - 1) : index < 0 || index === items.length - 1 ? 0 : index + 1;
      items[next]?.focus();
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.body.style.overflow = overflow;
      previous?.focus?.();
    };
  }, []);

  const unlock = useMutation({
    mutationFn: (body: { password: string; code: string }) => staffApi.unlock(body),
    onSuccess: () => onUnlocked(),
    onError: (error: unknown) => {
      if (isApiError(error) && error.code === 'ADMIN_AUTH_REQUIRED') {
        onSignedOut();
        return;
      }
      setFormError(errorText(error));
      setCode('');
      codeRef.current?.focus();
    },
  });

  const submit = (value: string = code) => {
    if (unlock.isPending) return;
    const next: { password?: string; code?: string } = {};
    if (!password) next.password = t('admin.login.passwordRequired');
    if (!/^\d{6}$/.test(value)) next.code = t('admin.login.codeRequired');
    setFieldErrors(next);
    setFormError(null);
    if (Object.keys(next).length > 0) return;
    unlock.mutate({ password, code: value });
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    submit();
  };

  return (
    <div className="fixed inset-0 z-[55] flex items-center justify-center bg-bg px-4 text-fg" data-lock-screen="">
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="w-full max-w-sm rounded-2xl border border-border bg-surface p-6 shadow-xl"
      >
        <p className="bg-brand-gradient bg-clip-text text-lg font-bold text-transparent">{t('admin.appName')}</p>
        <h2 id={titleId} className="mt-2 text-xl font-semibold">
          {t('admin.lock.title')}
        </h2>
        <p className="mt-1 text-fg-muted" data-lock-staff="">
          {staffName}
        </p>
        <form className="mt-5 flex flex-col gap-4" noValidate onSubmit={onSubmit}>
          <Field label={t('admin.login.password')} error={fieldErrors.password}>
            <TextInput ref={passwordRef} type="password" name="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
          </Field>
          <Field label={t('admin.login.code')} error={fieldErrors.code}>
            <CodeInput
              ref={codeRef}
              name="code"
              value={code}
              onChange={setCode}
              onComplete={(value) => {
                if (password) submit(value);
              }}
            />
          </Field>
          {formError && (
            <p role="alert" className="flex items-start gap-1.5 text-sm text-status-danger-fg">
              <span aria-hidden="true" className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-status-danger" />
              <span>{formError}</span>
            </p>
          )}
          <Button type="submit" block loading={unlock.isPending}>
            {t('admin.lock.unlock')}
          </Button>
          <Button variant="text" onClick={onSignedOut}>
            {t('admin.header.logout')}
          </Button>
        </form>
      </div>
    </div>
  );
}
